import { useMemo, useState } from "react";
import {
  Button,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Typography,
  Upload,
} from "antd";
import { PaperClipOutlined } from "@ant-design/icons";
import type { UploadFile } from "antd/es/upload/interface";
import { useSelect } from "@refinedev/core";
import { useTranslation } from "react-i18next";

import type { TicketCategory, TicketDetail } from "../../types/ticket";
import { ticketService } from "../../services/api/ticketService";
import { RichTextEditor } from "../../components/common/RichTextEditor";

const { Text } = Typography;

/** Nombre de la categoría "Solicitud de auditoría de productor" tal
 *  como la creó la migración 0009. Al detectarla, el modal muestra
 *  un selector adicional de productor para vincular el ticket a la
 *  ficha (evita parsear el texto libre en el backoffice). */
const AUDIT_REQUEST_CATEGORY_NAME = "Solicitud de auditoría de productor";

interface FormValues {
  subject: string;
  category: number;
  producer?: number;
}

interface Props {
  open: boolean;
  onClose: () => void;
  categories: TicketCategory[];
  onCreated: (ticket: TicketDetail) => void;
  onError: (err: any) => void;
}

export const TicketCreateModal = ({
  open,
  onClose,
  categories,
  onCreated,
  onError,
}: Props) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<FormValues>();
  const [bodyHtml, setBodyHtml] = useState("");
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const categoryValue = Form.useWatch("category", form);
  const selectedCategory = useMemo(
    () => categories.find((c) => c.id === categoryValue),
    [categories, categoryValue],
  );
  const isAuditRequest =
    selectedCategory?.name === AUDIT_REQUEST_CATEGORY_NAME;

  // Productores para el selector cuando aplica. Solo se cargan on
  // demand (queryOptions.enabled) para no pegar al endpoint en cada
  // apertura del modal. El backend ya scopea por país si el usuario
  // es Eurosemillas — el listado sale filtrado sin trabajo extra.
  const { options: producerOptions } = useSelect({
    resource: "v1/producers",
    optionLabel: "name",
    optionValue: "id",
    pagination: { pageSize: 1000 },
    queryOptions: { enabled: isAuditRequest },
  });

  const reset = () => {
    form.resetFields();
    setBodyHtml("");
    setFiles([]);
  };

  const onSubmit = async () => {
    let values: FormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    if (!bodyHtml || bodyHtml === "<p><br></p>") {
      onError({ message: "Empty body" });
      return;
    }
    if (isAuditRequest && !values.producer) {
      onError({
        message: t(
          "tickets.audit_request_producer_required",
          "Selecciona el productor al que quieres auditar.",
        ),
      });
      return;
    }
    setSubmitting(true);
    try {
      const ticket = await ticketService.createTicket({
        subject: values.subject,
        category: values.category,
        body_html: bodyHtml,
        producer: isAuditRequest ? values.producer : undefined,
        attachments: files
          .map((f) => f.originFileObj as File | undefined)
          .filter((f): f is File => !!f),
      });
      reset();
      onCreated(ticket);
    } catch (err) {
      onError(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title={t("tickets.create_title")}
      onCancel={() => {
        reset();
        onClose();
      }}
      onOk={onSubmit}
      okText={t("tickets.create")}
      cancelText={t("common.cancel")}
      confirmLoading={submitting}
      width={720}
      destroyOnClose
    >
      <Form form={form} layout="vertical" requiredMark={false}>
        <Form.Item
          label={t("tickets.subject")}
          name="subject"
          rules={[{ required: true }]}
        >
          <Input
            placeholder={t("tickets.ph_subject")}
            maxLength={200}
            showCount
          />
        </Form.Item>
        <Form.Item
          label={t("tickets.category")}
          name="category"
          rules={[{ required: true }]}
        >
          <Select
            placeholder={t("tickets.ph_category")}
            options={categories.map((c) => ({ value: c.id, label: c.name }))}
            getPopupContainer={(node) =>
              (node?.parentElement as HTMLElement) ?? document.body
            }
          />
        </Form.Item>
        {/* Solo visible cuando la categoría seleccionada es la de
             solicitud de auditoría — obligatorio en ese caso. Reusa
             el mismo endpoint /v1/producers que ya está scoped por
             país para el usuario Eurosemillas. */}
        {isAuditRequest && (
          <Form.Item
            label={t(
              "tickets.producer_to_audit",
              "Productor a auditar",
            )}
            name="producer"
            rules={[
              {
                required: true,
                message: t(
                  "tickets.audit_request_producer_required",
                  "Selecciona el productor al que quieres auditar.",
                ),
              },
            ]}
            extra={
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t(
                  "tickets.producer_scope_hint",
                  "Solo se listan los productores de tu país.",
                )}
              </Text>
            }
          >
            <Select
              showSearch
              placeholder={t(
                "tickets.ph_producer",
                "Buscar productor…",
              )}
              options={producerOptions}
              optionFilterProp="label"
              getPopupContainer={(node) =>
                (node?.parentElement as HTMLElement) ?? document.body
              }
            />
          </Form.Item>
        )}
        <Form.Item label={t("tickets.subject")} required style={{ display: "none" }}>
          {/* keep label-required visual style consistent */}
        </Form.Item>
        <Form.Item label={t("tickets.subject", "Body")}>
          <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 6 }}>
            {t("tickets.ph_body")}
          </Text>
          <RichTextEditor
            value={bodyHtml}
            onChange={setBodyHtml}
            placeholder={t("tickets.ph_body")}
            minHeight={180}
          />
        </Form.Item>
        <Form.Item label={t("tickets.attachments")}>
          <Upload
            beforeUpload={() => false}
            multiple
            fileList={files}
            onChange={({ fileList }) => setFiles(fileList)}
          >
            <Button icon={<PaperClipOutlined />}>{t("tickets.add_attachment")}</Button>
          </Upload>
        </Form.Item>
        <Space size={4}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t("tickets.subtitle")}
          </Text>
        </Space>
      </Form>
    </Modal>
  );
};

export default TicketCreateModal;
